import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'ng2-app/models/dto/lender-user.model';
import { Select, Store } from '@ngxs/store';
import { combineLatest, filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { UserSmsThreadsState } from 'ng2-app/modules/today/state/sms/user-sms-state';
import { SmsState } from 'ng2-app/sms/sms-store/sms.state';
import { BorrowerState } from 'ng2-app/store/borrower/borrower/borrower.state';
import { Borrower } from 'ng2-app/models/dto/borrower.model';
import {
  GetUserSmsThreads,
  GetUserUnknownSms,
  SetActiveThreadBorrowerId,
  SetActiveUnknownSms, UnsetActiveUserSms, UnsetActiveUnknownSms
} from 'ng2-app/modules/today/state/sms/user-sms.action';
import { UserActionItemsState } from 'ng2-app/modules/today/state/user-action-items.state';
import { AddBorrowerToSms, MarkSmsRead, MarkSmsUnread } from 'ng2-app/sms/sms-store/sms.actions';
import { Sms } from 'ng2-app/sms/sms-service/sms.model';
import { ClearBorrowerStore } from 'ng2-app/store/borrower/borrower/borrower.actions';
import { LinkMessageToBorrowerDialogComponent } from 'ng2-app/modules/task-management/components/unknown-inbox-items/link-message-to-borrower-dialog/link-message-to-borrower-dialog.component';
import { AddNewLeadFormComponent } from 'ng2-app/add-new-lead/add-new-lead-form/add-new-lead-form.component';
import { MatDialog } from '@angular/material/dialog';
import { TodayBadgeCountsState } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.state';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-user-sms-list-view',
  templateUrl: './user-sms-list-view.component.html',
  styleUrls: ['./user-sms-list-view.component.scss']
})
export class UserSmsListViewComponent implements OnInit, OnDestroy {

  @Select(BorrowerState.borrower) borrower$: Observable<Borrower>;
  @Select(BorrowerState.loading) borrowerLoading$: Observable<boolean>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(UserActionItemsState.user) user$: Observable<any>;
  @Select(UserSmsThreadsState.threads) smsThreads$: Observable<any[]>;
  @Select(UserSmsThreadsState.listLoading) smsThreadsLoading$: Observable<boolean>;
  @Select(UserSmsThreadsState.activeThreadBorrowerId) activeThreadBorrowerId$: Observable<number>;
  // we'll call this borrowerSmsLoading for now until we have SMS built as threads so we can fetch threads individually
  @Select(SmsState.loadingInitial) borrowerSmsLoading$: Observable<boolean>;
  @Select(UserSmsThreadsState.unknownSms) unknownSms$: Observable<Sms[]>;
  @Select(UserSmsThreadsState.activeUnknownSms) activeUnknownSms$: Observable<Sms>;
  @Select(UserSmsThreadsState.smsTotal) smsTotal$: Observable<number>;
  @Select(UserSmsThreadsState.unknownSmsTotal) unknownSmsTotal$: Observable<number>;
  @Select(UserSmsThreadsState.showLoadMore) showLoadMore$: Observable<boolean>;
  @Select(TodayBadgeCountsState.unreadUnknownSmsThreads) unreadUnknownSmsThreads$: Observable<number>;
  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;


  destroyed$ = new Subject<boolean>();
  listEmpty$: Observable<boolean>;
  showLoadMoreButton$: Observable<boolean>;
  contextMenuPosition = { x: '0px', y: '0px' };

  // is the currently logged-in user the selected user (UserActionItemsState.user)
  actionItemsUserIsAuthUser: boolean = true;

  page: number = 1;
  pageSize: number = 20;
  selectedType: string = 'unread';
  sortDirection: string = 'desc';
  jsonParse = JSON.parse;
  jsonStr = JSON.stringify;

  constructor(
    private store: Store,
    public dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    combineLatest([this.user$, this.lenderUser$])
      .pipe(
        takeUntil(this.destroyed$),
        map(([user, lenderUser]) => {
          return user.id === lenderUser.id;
        })
      ).subscribe( result => {
      this.actionItemsUserIsAuthUser = result;
      this.resetAndFetch();
    });

    this.listEmpty$ = combineLatest([this.smsThreads$, this.unknownSms$])
      .pipe(
        takeUntil(this.destroyed$),
        map(([smsThreads, unknownSms]) => {
          return this.selectedType === 'unknown' ?
            unknownSms.length < 1 :
            smsThreads.length < 1;
        })
      );

    this.activeUnknownSms$.pipe(
      takeUntil(this.destroyed$),
      filter((res) => !!res),
    ).subscribe( unknownSms => {
      if(!unknownSms.read) {
        this.store.dispatch(new MarkSmsRead(unknownSms.lenderUserId, [unknownSms.id]));
      }
    });

    this.showLoadMoreButton$ =
      combineLatest([
        this.borrowerLoading$,
        this.smsThreadsLoading$,
        this.showLoadMore$,
      ]).pipe(
        takeUntil(this.destroyed$),
        map(([borrowerLoading, smsLoading, showLoadMore ]) => {
          return (!borrowerLoading && !smsLoading) && showLoadMore;
        })
      );
  }

  getSmsThreads(reset: boolean) {
    if(this.selectedType === 'unknown') {
      return this.getUnkownSms(reset);
    }
    this.user$.pipe(
      take(1)
    ).subscribe( ({ id }) => {
      this.store.dispatch(new GetUserSmsThreads(id, reset, this.page, this.selectedType, this.sortDirection));
    });

  }

  getUnkownSms(reset: boolean) {
    this.user$.pipe(
      take(1)
    ).subscribe( ({ id }) => {
      this.store.dispatch(new GetUserUnknownSms(id, reset, this.page));
    });
  }

  resetAndFetch(): void {
    // changing type filters or sorting should reset the total fetch count / pagination
    this.page = 1;
    if(this.selectedType === 'unknown') {
      this.store.dispatch(new ClearBorrowerStore());
      this.store.dispatch(new UnsetActiveUnknownSms());
      this.getUnkownSms(true);
    } else {
      this.store.dispatch(new UnsetActiveUserSms());
      this.getSmsThreads(true);
    }
  }

  fetchThread(borrowerId) {
    this.activeThreadBorrowerId$.pipe(take(1))
      .subscribe( currentBorrowerId => {
        return borrowerId !== currentBorrowerId ?
          this.store.dispatch( new SetActiveThreadBorrowerId(borrowerId, false)) :
          false;
      });
  }

  loadMore(): void {
    const unread = this.selectedType === 'unread';
    this.page = unread ? 1 : ++this.page;
    this.getSmsThreads(unread);
  }

  linkSmsToBorrower(uknownSms: Sms) {
    const dialogRef = this.dialog.open(LinkMessageToBorrowerDialogComponent, {
      data: { message: uknownSms }
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe(data => {
      if (data) {
        this.store.dispatch(new AddBorrowerToSms(uknownSms.id, data.borrower))
          .pipe(take(1))
          .subscribe(() => {
            this.resetAndFetch();
          });
      }
    });
  }

  linkSmsToNewBorrower(uknownSms: Sms) {
    const dialogRef = this.dialog.open(AddNewLeadFormComponent, {
      width: '28rem',
      maxHeight: '100vh',
      panelClass: 'addNewLeadDialogContainer',
      data: {
        navigateToNewBorrower: false
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(data => {
      if (data) {
        this.store.dispatch(new AddBorrowerToSms(uknownSms.id, data.borrower))
          .pipe(take(1))
          .subscribe(() => {
            this.resetAndFetch();
          });
      }
    });
  }

  setActiveUnknownSms(sms: Sms) {
    this.store.dispatch( new SetActiveUnknownSms(sms));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.store.dispatch(new UnsetActiveUserSms());
    this.store.dispatch(new UnsetActiveUnknownSms());
  }

  onContextMenu($event, thread) {
    $event.preventDefault();
    this.contextMenuPosition.x = $event.clientX + 'px';
    this.contextMenuPosition.y = $event.clientY + 'px';
    this.contextMenu.menuData = { thread };
    this.contextMenu.menu?.focusFirstItem('mouse');
    this.contextMenu.openMenu();
  }

  contextMenuMarkUnread() {
    const thread = this.contextMenu.menuData.thread;
    if (thread && thread.borrowerId) {
      this.user$.pipe(take(1)).subscribe( user => {
        this.store.dispatch( new MarkSmsUnread(user.id, [], thread.borrowerId));
      });
    } else if (thread && !thread.borrowerId) {
      this.user$.pipe(take(1)).subscribe( user => {
        this.store.dispatch( new MarkSmsUnread(user.id, [thread.id]));
      });
    }

  }

}
